import {
  addDays,
  addMonths,
  addQuarters,
  addWeeks,
  addYears,
  format,
  isValid,
  startOfMonth,
  startOfWeek,
  startOfYear,
} from "date-fns";
import { fromJS } from "immutable";
import { UnifiedChartData, UnifiedSeries } from "./components/charts/types";
import { search } from "../api/ProjectApi";

export const getValueKey = (measureBy) => {
  if (measureBy === "dealCount" || measureBy === "dealLength") {
    return measureBy;
  }
  const valueKeyMap = {
    dealValue: "dealValueSum",
    dealAvg: "dealValueAvg",
  };
  return valueKeyMap[measureBy] || measureBy;
};

export const convertChartItem = (group, chartItem) => {
  if (chartItem) {
    return {
      ...chartItem,
      groupId: group.value,
      groupLabel: group.label,
    };
  }
  return {
    groupId: "",
    groupLabel: "Unknown",
    dealValueSum: 0.0,
    dealValueMin: 0.0,
    dealValueMax: 0.0,
    dealValueAvg: 0.0,
    dealCount: 0,
    dealLength: 0,
  };
};

export const datetimeFields = ["start", "end"];

export const getTimeItems = (unit, currentDate = new Date()) => {
  let start = startOfWeek(currentDate);
  const items = [];
  switch (unit) {
    case "day":
      start = startOfWeek(currentDate);
      for (let index = 0; index < 7; index++) {
        items.push(start);
        start = addDays(start, 1);
      }
      return items;
    case "week":
      start = startOfMonth(currentDate);
      for (let index = 0; index < 5; index++) {
        items.push(start);
        start = addWeeks(start, 1);
      }
      return items;
    case "month":
      start = startOfYear(currentDate);
      for (let index = 1; index <= 12; index++) {
        items.push(start);
        start = addMonths(start, 1);
      }
      return items;
    case "quarter":
      start = startOfYear(currentDate);
      for (let index = 0; index < 4; index++) {
        items.push(start);
        start = addQuarters(start, 1);
      }
      return items;
    case "year":
      start = addYears(currentDate, -5);
      for (let index = 0; index < 12; index++) {
        items.push(start);
        start = addYears(start, 1);
      }
      return items;
    default:
      return items;
  }
};

export const formatTimeLabel = (groupId, dateUnit) => {
  const date = new Date(groupId);
  if (isValid(date)) {
    if (dateUnit == "day") {
      return format(date, "EEE dd");
    }
    if (dateUnit == "week") {
      return `Week ${format(date, "II")}`;
    }
    if (dateUnit == "month") {
      return format(date, "MMM yyyy");
    }
    if (dateUnit == "year") {
      return format(date, "yyyy");
    }
    if (dateUnit == "quarter") {
      return format(date, "QQQ");
    }
  }
  return "";
};

export const dateUnitFields = [
  {
    label: "Daily",
    value: "day",
  },
  {
    label: "Weekly",
    value: "week",
  },
  {
    label: "Monthly",
    value: "month",
  },
  {
    label: "Quarterly",
    value: "quarter",
  },
  {
    label: "Yearly",
    value: "year",
  },
];

export const segmentByFields = {
  SALE: [
    {
      label: "Sale funnel",
      value: "bucketId",
    },
    {
      label: "Stage",
      value: "stage",
    },
    {
      label: "Status",
      value: "status",
    },
    {
      label: "Assignees",
      value: "assignees",
    },
  ],
  COMPANY: [
    {
      label: "Name",
      value: "name",
    },
    {
      label: "Owners",
      value: "assignees",
    },
    {
      label: "Labels",
      value: "labels",
    },
    {
      label: "Group",
      value: "bucketId",
    },
  ],
  CONTACT: [
    {
      label: "Name",
      value: "name",
    },
    {
      label: "Owners",
      value: "assignees",
    },
    {
      label: "Labels",
      value: "labels",
    },
    {
      label: "Group",
      value: "bucketId",
    },
  ],
  PRODUCT: [
    {
      label: "Name",
      value: "name",
    },
    {
      label: "Owners",
      value: "assignees",
    },
    {
      label: "Labels",
      value: "labels",
    },
    {
      label: "Group",
      value: "bucketId",
    },
  ],
  ACTIVITY: [
    {
      label: "Done",
      value: "done",
    },
    {
      label: "Summary",
      value: "summary",
    },
    {
      label: "Owners",
      value: "assignees",
    },
  ],
};

export const viewByFields = {
  SALE: [
    {
      label: "Sale funnel",
      value: "bucketId",
    },
    {
      label: "Stage",
      value: "stage",
    },
    {
      label: "Status",
      value: "status",
    },
    {
      label: "Assignees",
      value: "assignees",
    },
    {
      label: "Deal created on",
      value: "start",
    },
    {
      label: "Expected close date",
      value: "end",
    },
  ],
  COMPANY: [
    {
      label: "Name",
      value: "name",
    },
    {
      label: "Owners",
      value: "assignees",
    },
    {
      label: "Labels",
      value: "labels",
    },
    {
      label: "Group",
      value: "bucketId",
    },
  ],
  CONTACT: [
    {
      label: "Name",
      value: "name",
    },
    {
      label: "Owners",
      value: "assignees",
    },
    {
      label: "Labels",
      value: "labels",
    },
    {
      label: "Group",
      value: "bucketId",
    },
  ],
  PRODUCT: [
    {
      label: "Name",
      value: "name",
    },
    {
      label: "Owners",
      value: "assignees",
    },
    {
      label: "Labels",
      value: "labels",
    },
    {
      label: "Group",
      value: "bucketId",
    },
  ],
  ACTIVITY: [
    {
      label: "Done",
      value: "done",
    },
    {
      label: "Summary",
      value: "summary",
    },
    {
      label: "Owners",
      value: "assignees",
    },
  ],
};

export const measureByFields = {
  SALE: [
    {
      label: "Deal count",
      value: "dealCount",
      valueFunc: "count",
    },
    {
      label: "Average value",
      value: "dealAvg",
      valueFunc: "avg",
    },
    {
      label: "Deal value",
      value: "dealValue",
      valueFunc: "sum",
    },
  ],
  COMPANY: [
    {
      label: "Account count",
      value: "dealCount",
      valueFunc: "count",
    },
  ],
  CONTACT: [
    {
      label: "Contact count",
      value: "dealCount",
      valueFunc: "count",
    },
  ],
  PRODUCT: [
    {
      label: "Product count",
      value: "dealCount",
      valueFunc: "count",
    },
  ],
  ACTIVITY: [
    {
      label: "Activity count",
      value: "dealCount",
      valueFunc: "count",
    },
  ],
};

export const getViewByLabel = (type, viewBy) => {
  return (
    viewByFields[type].find((item) => item.value === viewBy) || {
      label: viewBy,
      value: viewBy,
    }
  );
};

export const getMeasureByLabel = (type, fieldId) => {
  return (
    measureByFields[type].find((item) => item.value === fieldId) || {
      label: fieldId,
      value: fieldId,
    }
  );
};

export const getDateUnit = (goal) => {
  const goalFrequency = goal.get("frequency");
  if (goalFrequency == "Daily") {
    return "day";
  }
  if (goalFrequency == "Weekly") {
    return "week";
  }
  if (goalFrequency == "Quarterly") {
    return "quarter";
  }
  if (goalFrequency == "Monthly") {
    return "month";
  }
  if (goalFrequency == "Yearly") {
    return "year";
  }
  return "month";
};

export const handleDataBarChart = (
  data,
  viewBy,
  measureBy,
  dateUnit,
  groups,
  segments
): UnifiedChartData => {
  const dataKey = getValueKey(measureBy);

  const chartDataKey = (segment) =>
    datetimeFields.includes(viewBy) ? dataKey : segment.get("value");

  let chartData = [...data];
  if (datetimeFields.includes(viewBy)) {
  } else if (groups) {
    const items = groups.toJS().map((group) => {
      const itemData = data.find((item) => item.groupId == group.value) || {};
      return convertChartItem(group, itemData);
    });
    chartData = [...items];
  }

  const groupedData = chartData.reduce((acc, item) => {
    const groupKey = item["groupId"] || "";
    const segmentKey = item["segmentId"] || "None";
    if (!acc[groupKey]) acc[groupKey] = {};
    if (!acc[groupKey][segmentKey]) acc[groupKey][segmentKey] = 0;
    const value = item[dataKey] || 0;
    acc[groupKey][segmentKey] += value;
    return acc;
  }, {});

  const chartDataV2 = Object.keys(groupedData).map((groupKey) => {
    const segments = groupedData[groupKey];
    const group = groups.toJS().find((item) => item.value == groupKey);
    return {
      groupId: groupKey,
      groupLabel: group?.label || groupKey,
      ...segments,
    };
  });

  const chartDataFinal = datetimeFields.includes(viewBy) ? chartData : chartDataV2;

  const labels = chartDataFinal.map((item) =>
    datetimeFields.includes(viewBy) ? formatTimeLabel(item.groupId, dateUnit) : item.groupLabel
  );
  const series: UnifiedSeries[] = [];
  segments.map((segment) => {
    const datas = chartDataFinal.map((item) => item[chartDataKey(segment)] || 0);
    const serie: UnifiedSeries = {
      dataKey: chartDataKey(segment),
      name: segment.get("label"),
      data: datas,
    };
    series.push(serie);
  });

  return { labels, series };
};

// Helper function for comparison labels
const getComparisonLabel = (comparisonBy: string, type: "current" | "previous"): string => {
  const labels = {
    lastYear: { current: "This Year", previous: "Last Year" },
    lastMonth: { current: "This Month", previous: "Last Month" },
    lastWeek: { current: "This Week", previous: "Last Week" },
    lastQuarter: { current: "Current Period", previous: "Last Quarter" },
  };
  return labels[comparisonBy]?.[type] || (type === "current" ? "Current" : "Previous");
};

export const handleDataLineChart = (
  currentData = [],
  previousData = [],
  viewBy,
  measureBy,
  dateUnit,
  groups,
  comparisonBy
): UnifiedChartData => {
  // Process current data
  let currentChartData = [...currentData];
  if (datetimeFields.includes(viewBy)) {
  } else if (groups) {
    const items = groups.toJS().map((group) => {
      const itemData = currentData.find((item) => item.groupId == group.value) || {};
      return convertChartItem(group, itemData);
    });
    currentChartData = [...items];
  }

  const dataKey = getValueKey(measureBy);
  const labels = currentChartData.map((item) =>
    datetimeFields.includes(viewBy) ? formatTimeLabel(item.groupId, dateUnit) : item.groupLabel
  );
  const currentDatas = currentChartData.map((item) => item[dataKey] || 0);

  const currentSerie: UnifiedSeries = {
    dataKey: "current",
    name:
      comparisonBy && comparisonBy !== "none"
        ? getComparisonLabel(comparisonBy, "current")
        : "value",
    data: currentDatas,
  };

  // Only show second line when comparison is enabled
  if (!comparisonBy || comparisonBy === "none") {
    return {
      labels,
      series: [currentSerie],
    };
  }

  // If no previous data, create default data with zeros
  if (previousData.length === 0) {
    const defaultPreviousData = labels.map(() => 0);
    const defaultPreviousSerie: UnifiedSeries = {
      dataKey: "previous",
      name: getComparisonLabel(comparisonBy, "previous"),
      data: defaultPreviousData,
    };

    return {
      labels,
      series: [currentSerie, defaultPreviousSerie],
    };
  }

  // Process previous data
  let previousChartData = [...previousData];
  if (datetimeFields.includes(viewBy)) {
  } else if (groups) {
    const items = groups.toJS().map((group) => {
      const itemData = previousData.find((item) => item.groupId == group.value) || {};
      return convertChartItem(group, itemData);
    });
    previousChartData = [...items];
  }

  const previousDatas = previousChartData.map((item) => item[dataKey] || 0);

  const previousSerie: UnifiedSeries = {
    dataKey: "previous",
    name: getComparisonLabel(comparisonBy, "previous"),
    data: previousDatas,
  };

  return {
    labels,
    series: [currentSerie, previousSerie],
  };
};

export const handleDataPieChart = (data, viewBy, measureBy, groups, segments): UnifiedChartData => {
  let chartData = [...data];
  if (datetimeFields.includes(viewBy)) {
  } else if (groups) {
    const items = groups.toJS().map((group) => {
      const itemData = data.find((item) => item.groupId == group.value) || {};
      return convertChartItem(group, itemData);
    });
    chartData = [...items];
  }
  const dataKey = getValueKey(measureBy);

  // Step 1: Aggregate dataKey by segmentId
  const groupedData = chartData.reduce((acc, item) => {
    const segment = segments.toJS().find((seg) => seg.value === item.segmentId);
    const key = segment ? segment.label : "UnSegmented";
    const value = Number(item[dataKey]);
    acc[key] = (acc[key] || 0) + (isNaN(value) ? 0 : value);
    return acc;
  }, {});

  // Step 2: Convert to Recharts format
  const segmentsList = segments.toJS();
  const labels = segmentsList.map((item) => item.label);
  const datas = segmentsList.map((item) => groupedData[item.label] || 0);
  const serie: UnifiedSeries = {
    dataKey: "value",
    name: "value",
    data: datas,
  };

  return {
    labels,
    series: [serie],
  };
};

export const handleDataGoalChart = (data, goal, currency): UnifiedChartData => {
  const getYAxisLabel = () => {
    if (goal.get("trackingMetric") == "Value") {
      return `Amount (${currency})`;
    }
    if (goal.get("trackingMetric") == "Count") {
      return "Number of Deals";
    }
  };
  const dateUnit = getDateUnit(goal);
  const trackingFrom = goal.get("trackingFrom");
  const timeItems = getTimeItems(dateUnit, new Date(trackingFrom));
  const items = data.map((item) => {
    return {
      ...item,
      groupLabel: formatTimeLabel(item.groupId, dateUnit),
    };
  });

  const getItem = (dateItem) => {
    return items.find((group) => group.groupLabel == formatTimeLabel(dateItem, dateUnit)) || {};
  };

  const dataItems = timeItems.map((item) => {
    const itemValue = {
      groupLabel: formatTimeLabel(item, dateUnit),
      dealValueSum: 0,
      dealCount: 0,
      ...getItem(item),
    };
    return {
      ...itemValue,
    };
  });

  const labels = dataItems.map((item) => item.groupLabel);
  const datas = dataItems.map((item) =>
    goal.get("trackingMetric") == "Count" ? item.dealCount : item.dealValueSum
  );
  const serie: UnifiedSeries = {
    dataKey: getYAxisLabel(),
    name: getYAxisLabel(),
    data: datas,
  };

  return {
    labels,
    series: [serie],
  };
};

export const filterDataGoalChart = (projectId, goal) => {
  const getScopeFilter = async () => {
    let scopeFilters = {};
    if (goal.getIn(["scope", "type"]) == "User") {
      scopeFilters = {
        assignees: {
          operator: "is",
          values: [goal.getIn(["scope", "value", "value"])],
        },
      };
    } else if (goal.getIn(["scope", "type"]) == "Account") {
      const result = await search({
        projectId,
        pageSize: 100,
        filters: {
          account: {
            operator: "is",
            values: [goal.getIn(["scope", "value", "value"])],
          },
        },
        types: ["CONTACT"],
      });
      const contactIds = result.pageResult.content.map((item) => item.id);
      scopeFilters = {
        contact: {
          operator: "in",
          values: contactIds,
        },
      };
    }
    return scopeFilters;
  };

  const getDealTypeFilter = () => {
    let filters = {};
    if (goal.get("type") == "ADDED_SALE") {
      filters = {};
    } else if (goal.get("type") == "WON_SALE") {
      filters = {
        stage: {
          operator: "in",
          values: ["Won"],
        },
      };
    } else if (goal.get("type") == "PROGRESSED_SALE") {
      filters = {
        status: {
          operator: "in",
          values: ["Open"],
        },
      };
    }
    return filters;
  };

  const buildFilters = async () => {
    const bucketIds = goal.get("buckets").map((item) => item.get("value"));
    const filters = fromJS({
      ...(await getScopeFilter()),
      ...getDealTypeFilter(),
      bucketId: {
        operator: "in",
        values: bucketIds,
      },
    });
    return filters;
  };

  return buildFilters();
};
